import { useEffect, useState } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import type { GridCustomCellProps } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Link, useNavigate } from 'react-router-dom';
import type { TenantDto, TenantPoolCapacityDto } from '@shared/api-types';
import { adminApiFetch } from '../../lib/adminApiClient';
import { useRegion } from '../../lib/regionContext';
import { useElementWidth, toResponsiveWidth } from '@shared/useElementWidth';
import { TenantPoolCapacity } from './TenantPoolCapacity';

const TENANT_STATUS_PROVISIONING = 0;

// While a tenant is Provisioning its database is being created on the Jobs host
// (AAI-129); re-fetch the list on this cadence until it turns Active.
const PROVISIONING_POLL_MS = 10_000;

const STATUS_LABEL: Record<number, string> = {
  0: 'Provisioning',
  1: 'Active',
  2: 'Suspended',
  // Deprovisioning = soft-deleted (AAI-151); only the delete flow sets it.
  3: 'Deleted',
};

const OVERAGE_LABEL: Record<number, string> = {
  0: 'Monthly',
  1: 'Annual',
};

function EditLinkCell(props: GridCustomCellProps) {
  const id = (props.dataItem as TenantDto).id;
  return (
    <td>
      <Link to={`/tenants/${id}`}>Edit</Link>
    </td>
  );
}

export function Tenants() {
  const navigate = useNavigate();
  const { region } = useRegion();
  const [rows, setRows] = useState<TenantDto[] | null>(null);
  // Container-measured column shedding, matching the customer app's grids: the
  // grid's own width, not the viewport's, because the drawer rail changes what
  // is available by ~200px. See DESIGN.md ("Responsive").
  const [gridRef, gridWidth] = useElementWidth();
  const w = toResponsiveWidth(gridWidth);
  const [error, setError] = useState<string | null>(null);
  const [pollFailed, setPollFailed] = useState(false);
  // Soft-deleted tombstones (AAI-151) are hidden unless asked for.
  const [showDeleted, setShowDeleted] = useState(false);
  const listUrl = showDeleted ? '/api/admin/tenants?includeDeleted=true' : '/api/admin/tenants';
  // Tenant-pool capacity (AAI-144) — read once; a failure never blocks the list.
  const [pools, setPools] = useState<TenantPoolCapacityDto | null>(null);
  const [poolsError, setPoolsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await adminApiFetch<TenantPoolCapacityDto>('/api/admin/tenant-pools');
        if (!cancelled) setPools(data);
      } catch (err) {
        if (!cancelled) setPoolsError(err instanceof Error ? err.message : 'Failed to load tenant pools.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await adminApiFetch<TenantDto[]>(listUrl);
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load tenants.');
          setRows([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [listUrl]);

  // Poll while any row is Provisioning. Stops when none are, when a re-fetch
  // fails (the error shows above the grid; the last good rows stay), or on unmount.
  const anyProvisioning = (rows ?? []).some((r) => r.status === TENANT_STATUS_PROVISIONING);
  useEffect(() => {
    if (!anyProvisioning || pollFailed) return;
    let cancelled = false;
    let inFlight = false;
    const intervalId = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      void (async () => {
        try {
          const data = await adminApiFetch<TenantDto[]>(listUrl);
          if (!cancelled) setRows(data);
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Failed to refresh tenants.');
            setPollFailed(true);
          }
        } finally {
          inFlight = false;
        }
      })();
    }, PROVISIONING_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [anyProvisioning, pollFailed, listUrl]);

  const decorated = (rows ?? []).map((r) => ({
    ...r,
    statusLabel: STATUS_LABEL[r.status] ?? String(r.status),
    overageLabel: OVERAGE_LABEL[r.overagePeriod] ?? String(r.overagePeriod),
    poolLabel: pools?.databasePools[r.tenantDatabaseName] ?? '—',
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>Tenants</h2>
          <div style={{ color: '#64748b', fontSize: 13 }}>Region: {region.toUpperCase()}</div>
        </div>
        <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto', marginRight: 16 }}>
          <input
            type="checkbox"
            checked={showDeleted}
            onChange={(e) => {
              // A new list starts clean: no stale banner, and Provisioning polling
              // may resume after an earlier failed poll.
              setError(null);
              setPollFailed(false);
              setShowDeleted(e.target.checked);
            }}
            data-testid="show-deleted"
          />
          Show deleted
        </label>
        <Button themeColor="primary" onClick={() => navigate('/tenants/new')} data-testid="new-tenant-btn">
          New tenant
        </Button>
      </div>
      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}
      <TenantPoolCapacity capacity={pools} error={poolsError} />
      <div ref={gridRef}>
      <Grid
        data={decorated}
        style={{ height: 'calc(100dvh - 300px)' }}
      >
        {/* Shed billing detail and the MFA flag first — they are context, and
            each is visible on the tenant's own edit page. Name (identity),
            Status (state) and Actions never drop. */}
        <GridColumn field="name" title="Name" />
        <GridColumn field="edition" title="Edition" width="130px" />
        <GridColumn field="statusLabel" title="Status" width="120px" />
        {w >= 900 && <GridColumn field="includedCreditsPerPeriod" title="Included credits" width="160px" />}
        {w >= 780 && <GridColumn field="overageLabel" title="Overage" width="120px" />}
        {w >= 660 && <GridColumn field="mfaRequired" title="MFA required" width="120px" />}
        {w >= 1080 && <GridColumn field="poolLabel" title="Pool" width="190px" />}
        <GridColumn
          title="Actions"
          width="120px"
          cells={{ data: EditLinkCell }}
        />
      </Grid>
      </div>
    </div>
  );
}
